import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PhotoPicker from "@/components/admin/PhotoPicker";
import { errMsg } from "@/lib/errors";
import {
  Phone,
  Mail,
  MapPin,
  Plus,
  Trash2,
  Save,
  Sparkles,
  Camera,
  Upload,
  X,
  Image as ImageIcon,
  Home,
  Library,
  ArrowUp,
  ArrowDown,
  Film,
} from "lucide-react";

const LANGS = ["it", "en", "de", "fr"];
const LANG_LABEL = { it: "Italiano", en: "English", de: "Deutsch", fr: "Français" };
const SERVICE_ICONS = [
  // Generali / esistenti
  "wifi", "coffee", "ac", "parking", "concierge", "transfer", "pet", "tour",
  // Bellezza & benessere
  "parrucchiera", "scissors", "spa", "massaggio", "bath",
  // Cibo & bevande
  "ristorante", "food", "pizza", "gelato", "beer", "wine", "cake",
  // Delivery & shopping
  "delivery", "package", "shopping", "spesa", "gift",
  // Trasporti & attività
  "bike", "boat", "mare", "sole",
  // Salute
  "doctor", "farmacia", "baby",
  // Casa / vita
  "laundry", "maintenance", "bed", "work",
  // Multimedia / extra
  "photo", "music", "location", "call", "chat", "tv", "games", "books",
];

const emptyMl = () => ({ it: "", en: "", de: "", fr: "" });

const SUB_SECTIONS = [
  { id: "hero", label: "Home (Hero)", icon: Home },
  { id: "contact", label: "Contatti", icon: Phone },
  { id: "services", label: "Servizi", icon: Sparkles },
  { id: "attractions", label: "Attrazioni", icon: MapPin },
  { id: "showcase_gallery", label: "Galleria carosello", icon: ImageIcon },
];

export default function SiteContentTab() {
  const [active, setActive] = useState("hero");
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/site/content");
      setContent(r.data);
    } catch {
      toast.error("Errore caricamento contenuti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveSection = async (section, data) => {
    try {
      await api.put("/admin/site/content", { section, data });
      toast.success("Salvato");
      setContent((p) => ({ ...p, [section]: data }));
    } catch (err) {
      toast.error(errMsg(err, "Errore salvataggio"));
    }
  };

  if (loading || !content) return <div className="p-6 text-[#635F59]">Caricamento...</div>;

  return (
    <section data-testid="site-content-tab" className="space-y-4">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-1 inline-flex flex-wrap gap-1">
        {SUB_SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(s.id)}
              data-testid={`site-sub-${s.id}`}
              className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition ${
                active === s.id
                  ? "bg-[#1E3A5F] text-white"
                  : "text-[#635F59] hover:bg-[#FDFBF7]"
              }`}
            >
              <Icon className="w-4 h-4" />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5">
        {active === "hero" && (
          <HeroEditor data={content.hero || {}} onSave={(d) => saveSection("hero", d)} />
        )}
        {active === "contact" && (
          <ContactEditor data={content.contact} onSave={(d) => saveSection("contact", d)} />
        )}
        {active === "services" && (
          <ServicesEditor data={content.services || []} onSave={(d) => saveSection("services", d)} />
        )}
        {active === "attractions" && (
          <AttractionsEditor data={content.attractions || []} onSave={(d) => saveSection("attractions", d)} />
        )}
        {active === "showcase_gallery" && (
          <LiveGalleryEditor
            data={content.showcase_gallery || {}}
            onSave={(d) => saveSection("showcase_gallery", d)}
            sectionLabel="Galleria carosello"
            sectionHelp="Mostrata sotto la sezione Struttura come carosello rotante. Le foto si alternano automaticamente ogni 4-5 secondi."
          />
        )}
      </div>
    </section>
  );
}

/* ---------------- Contact ---------------- */
function ContactEditor({ data, onSave }) {
  const [form, setForm] = useState({
    address: data?.address || "",
    phones: data?.phones?.length ? data.phones : [{ label: "Reception", number: "" }],
    emails: data?.emails?.length ? data.emails : [{ label: "Info", address: "" }],
    whatsapp: data?.whatsapp || "",
    instagram: data?.instagram || "",
    facebook: data?.facebook || "",
  });

  const updPhone = (i, k, v) => {
    setForm((p) => ({ ...p, phones: p.phones.map((x, j) => (i === j ? { ...x, [k]: v } : x)) }));
  };
  const updEmail = (i, k, v) => {
    setForm((p) => ({ ...p, emails: p.emails.map((x, j) => (i === j ? { ...x, [k]: v } : x)) }));
  };

  return (
    <div className="space-y-5" data-testid="contact-editor">
      <h3 className="font-display text-xl text-[#1E3A5F]">Contatti pubblici</h3>
      <p className="text-xs text-[#635F59] -mt-3">Email, telefoni e indirizzo mostrati nella pagina Contatti.</p>

      <div>
        <Label>Indirizzo</Label>
        <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" data-testid="cm-address" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="flex items-center gap-2"><Phone className="w-4 h-4" /> Telefoni</Label>
          <Button size="sm" variant="outline" onClick={() => setForm((p) => ({ ...p, phones: [...p.phones, { label: "", number: "" }] }))} data-testid="cm-add-phone">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
        <div className="space-y-2">
          {form.phones.map((p, i) => (
            <div key={`ph-${i}`} className="flex gap-2" data-testid={`cm-phone-${i}`}>
              <Input placeholder="Etichetta (es. Reception)" value={p.label} onChange={(e) => updPhone(i, "label", e.target.value)} className="w-48" />
              <Input placeholder="+39 ..." value={p.number} onChange={(e) => updPhone(i, "number", e.target.value)} className="flex-1" />
              <Button variant="ghost" size="icon" onClick={() => setForm((s) => ({ ...s, phones: s.phones.filter((_, j) => j !== i) }))} className="text-rose-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
          <Button size="sm" variant="outline" onClick={() => setForm((p) => ({ ...p, emails: [...p.emails, { label: "", address: "" }] }))} data-testid="cm-add-email">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
        <div className="space-y-2">
          {form.emails.map((m, i) => (
            <div key={`em-${i}`} className="flex gap-2" data-testid={`cm-email-${i}`}>
              <Input placeholder="Etichetta (es. Info)" value={m.label} onChange={(e) => updEmail(i, "label", e.target.value)} className="w-48" />
              <Input placeholder="email@example.com" type="email" value={m.address} onChange={(e) => updEmail(i, "address", e.target.value)} className="flex-1" />
              <Button variant="ghost" size="icon" onClick={() => setForm((s) => ({ ...s, emails: s.emails.filter((_, j) => j !== i) }))} className="text-rose-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-2 border-t border-[#E8E2D9] mt-4 space-y-3">
        <h4 className="font-semibold text-[#1E3A5F] text-sm">Social</h4>
        <div>
          <Label>URL Instagram</Label>
          <Input
            value={form.instagram}
            onChange={(e) => setForm({ ...form, instagram: e.target.value })}
            placeholder="https://www.instagram.com/emmanuel_beb"
            className="mt-1.5 font-mono text-xs"
            data-testid="cm-instagram"
          />
        </div>
        <div>
          <Label>URL Facebook</Label>
          <Input
            value={form.facebook}
            onChange={(e) => setForm({ ...form, facebook: e.target.value })}
            placeholder="https://www.facebook.com/profile.php?id=..."
            className="mt-1.5 font-mono text-xs"
            data-testid="cm-facebook"
          />
          <p className="text-[10px] text-[#635F59] mt-1">Apri Facebook → vai sulla pagina del B&amp;B → copia l'URL dalla barra del browser e incollalo qui.</p>
        </div>
      </div>

      <div className="pt-2">
        <Button onClick={() => onSave(form)} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="cm-save">
          <Save className="w-4 h-4 mr-2" /> Salva
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Services ---------------- */
function ServicesEditor({ data, onSave }) {
  const [list, setList] = useState(data);
  const [lang, setLang] = useState("it");

  const upd = (i, k, v) => setList((p) => p.map((x, j) => (i === j ? { ...x, [k]: v } : x)));
  const updMl = (i, field, l, v) => setList((p) => p.map((x, j) => (i === j ? { ...x, [field]: { ...(x[field] || emptyMl()), [l]: v } } : x)));
  const move = (i, dir) => setList((p) => {
    const arr = [...p];
    const t = i + dir;
    if (t < 0 || t >= arr.length) return p;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    return arr;
  });

  return (
    <div className="space-y-4" data-testid="services-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F]">Servizi della struttura</h3>
          <p className="text-xs text-[#635F59]">Modifica titolo, descrizione e icona di ciascun servizio.</p>
        </div>
        <div className="flex gap-1">
          {LANGS.map((l) => (
            <button key={l} type="button" onClick={() => setLang(l)} className={`px-2.5 py-1 rounded text-xs font-semibold ${lang === l ? "bg-[#1E3A5F] text-white" : "bg-[#FDFBF7] border border-[#E8E2D9]"}`}>
              {LANG_LABEL[l]}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {list.map((s, i) => (
          <div key={`svc-${i}`} className="border border-[#E8E2D9] rounded-lg p-3 bg-[#FDFBF7]" data-testid={`svc-row-${i}`}>
            <div className="grid md:grid-cols-12 gap-2">
              <div className="md:col-span-2">
                <Label className="text-xs">Icona</Label>
                <select value={s.icon} onChange={(e) => upd(i, "icon", e.target.value)} className="w-full h-9 mt-1 bg-white border border-[#E8E2D9] rounded px-2 text-sm">
                  {SERVICE_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
              <div className="md:col-span-4">
                <Label className="text-xs">Titolo ({LANG_LABEL[lang]})</Label>
                <Input value={s.t?.[lang] || ""} onChange={(e) => updMl(i, "t", lang, e.target.value)} className="mt-1 h-9" />
              </div>
              <div className="md:col-span-5">
                <Label className="text-xs">Descrizione ({LANG_LABEL[lang]})</Label>
                <Input value={s.d?.[lang] || ""} onChange={(e) => updMl(i, "d", lang, e.target.value)} className="mt-1 h-9" />
              </div>
              <div className="md:col-span-1 flex items-end gap-1">
                <Button size="icon" variant="ghost" onClick={() => move(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => move(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => setList((p) => p.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <div className="md:col-span-12">
                <Label className="text-xs flex items-center gap-1">
                  <span>ℹ️ Info dettagliate ({LANG_LABEL[lang]})</span>
                  <span className="text-[10px] text-[#8A8680] font-normal italic">— opzionale, mostrate in un pop-up quando l&apos;ospite clicca il pulsante &quot;Info&quot;</span>
                </Label>
                <Textarea
                  rows={3}
                  value={s.info?.[lang] || ""}
                  onChange={(e) => updMl(i, "info", lang, e.target.value)}
                  placeholder={lang === "it"
                    ? "Es. Orari: 7:30–10:00. Buffet con dolci della casa, salumi, formaggi, frutta fresca, yogurt. Caffè espresso e cappuccino preparati al momento. Opzioni vegane e senza glutine su richiesta."
                    : "Detailed info shown in a popup when the guest clicks the 'Info' button on the service card."}
                  className="mt-1 text-sm resize-y"
                  data-testid={`svc-info-${i}`}
                />
              </div>
              <div className="md:col-span-12 grid md:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">🔗 Link prenotazione fornitore <span className="text-[#8A8680] font-normal">(sito web del servizio)</span></Label>
                  <Input
                    type="url"
                    placeholder="https://..."
                    value={s.url || ""}
                    onChange={(e) => upd(i, "url", e.target.value)}
                    className="mt-1 h-8 text-xs"
                    data-testid={`svc-url-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">💬 WhatsApp fornitore <span className="text-[#8A8680] font-normal">(prioritario sul link)</span></Label>
                  <Input
                    type="text"
                    placeholder="+39 333 0000000 oppure https://wa.me/39..."
                    value={s.whatsapp || ""}
                    onChange={(e) => upd(i, "whatsapp", e.target.value)}
                    className="mt-1 h-8 text-xs"
                    data-testid={`svc-whatsapp-${i}`}
                  />
                </div>
                <div className="md:col-span-2 text-[10px] text-[#635F59] italic -mt-1">
                  Genera il pulsante <strong>"Prenota il servizio"</strong> sulla pagina pubblica.
                  Se compili WhatsApp ha priorità (pulsante verde); altrimenti usa il link (pulsante marrone).
                </div>
              </div>
              <div className="md:col-span-12">
                <label className="flex items-start gap-2 text-xs text-[#1E3A5F] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!s.notify_host}
                    onChange={(e) => upd(i, "notify_host", e.target.checked)}
                    className="mt-0.5"
                    data-testid={`svc-notify-${i}`}
                  />
                  <span>
                    🔔 <strong>Avvisa l'Host</strong> — quando il cliente clicca questo servizio si apre un piccolo modulo (orario di arrivo previsto + note).
                    Tu ricevi email + Telegram per organizzarti.
                    <span className="text-[#635F59] italic ml-1">Utile per parrucchiera, consegna domicilio, ecc.</span>
                  </span>
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setList((p) => [...p, { icon: "wifi", t: emptyMl(), d: emptyMl(), info: emptyMl() }])} data-testid="svc-add">
          <Plus className="w-4 h-4 mr-2" /> Aggiungi servizio
        </Button>
        <Button onClick={() => onSave(list)} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="svc-save">
          <Save className="w-4 h-4 mr-2" /> Salva
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Attractions ---------------- */
function AttractionsEditor({ data, onSave }) {
  const [list, setList] = useState(data);
  const [lang, setLang] = useState("it");
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [pickerForIdx, setPickerForIdx] = useState(null);

  const upd = (i, k, v) => setList((p) => p.map((x, j) => (i === j ? { ...x, [k]: v } : x)));
  const updMl = (i, field, l, v) => setList((p) => p.map((x, j) => (i === j ? { ...x, [field]: { ...(x[field] || emptyMl()), [l]: v } } : x)));

  const uploadImage = async (i, file) => {
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "attractions");
      const res = await api.post("/admin/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd(i, "image", res.data.url);
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(errMsg(err, "Errore upload"));
    } finally {
      setUploadingIdx(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="attractions-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F]">Attrazioni nelle vicinanze</h3>
          <p className="text-xs text-[#635F59]">Punti di interesse mostrati nella sezione "Cosa fare in zona".</p>
        </div>
        <div className="flex gap-1">
          {LANGS.map((l) => (
            <button key={l} type="button" onClick={() => setLang(l)} className={`px-2.5 py-1 rounded text-xs font-semibold ${lang === l ? "bg-[#1E3A5F] text-white" : "bg-[#FDFBF7] border border-[#E8E2D9]"}`}>
              {LANG_LABEL[l]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {list.map((it, i) => (
          <div key={`atr-${i}`} className="border border-[#E8E2D9] rounded-lg p-3 bg-[#FDFBF7]" data-testid={`atr-row-${i}`}>
            <div className="flex gap-3">
              <div className="w-24 h-24 bg-white rounded overflow-hidden border border-[#E8E2D9] shrink-0 relative">
                {it.image ? (
                  <img src={it.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#635F59]"><ImageIcon className="w-6 h-6 opacity-30" /></div>
                )}
                <label className="absolute inset-0 cursor-pointer bg-black/0 hover:bg-black/30 flex items-center justify-center text-white opacity-0 hover:opacity-100 transition">
                  <Upload className="w-5 h-5" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(i, e.target.files[0])} />
                </label>
                {uploadingIdx === i && <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-xs">…</div>}
              </div>
              <div className="flex-1 space-y-2">
                <Input placeholder={`Titolo (${LANG_LABEL[lang]})`} value={it.t?.[lang] || ""} onChange={(e) => updMl(i, "t", lang, e.target.value)} className="h-8 text-sm" />
                <Textarea placeholder={`Descrizione (${LANG_LABEL[lang]})`} rows={2} value={it.d?.[lang] || ""} onChange={(e) => updMl(i, "d", lang, e.target.value)} className="text-sm resize-none" />
                <Input
                  type="url"
                  placeholder="Link di lancio (es. https://maps.google.com/...) — opzionale"
                  value={it.url || ""}
                  onChange={(e) => upd(i, "url", e.target.value)}
                  className="h-8 text-xs"
                  data-testid={`atr-url-${i}`}
                />
                <div className="flex gap-2 items-center">
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => setPickerForIdx(i)} data-testid={`atr-pick-${i}`}>
                    <Library className="w-3 h-3 mr-1" /> Da archivio
                  </Button>
                  <Button size="sm" variant="ghost" className="text-rose-600 h-7 text-xs px-2" onClick={() => setList((p) => p.filter((_, j) => j !== i))}>
                    <Trash2 className="w-3 h-3 mr-1" /> Elimina
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setList((p) => [...p, { image: "", t: emptyMl(), d: emptyMl() }])} data-testid="atr-add">
          <Plus className="w-4 h-4 mr-2" /> Aggiungi attrazione
        </Button>
        <Button onClick={() => onSave(list)} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="atr-save">
          <Save className="w-4 h-4 mr-2" /> Salva
        </Button>
      </div>
      <PhotoPicker
        open={pickerForIdx !== null}
        onClose={() => setPickerForIdx(null)}
        defaultCategory="attractions"
        onSelect={(url) => {
          if (pickerForIdx !== null) upd(pickerForIdx, "image", url);
        }}
      />
    </div>
  );
}

/* ---------------- Live Gallery ---------------- */
function LiveGalleryEditor({ data, onSave, sectionLabel, sectionHelp }) {
  const [form, setForm] = useState({
    title: { ...emptyMl(), ...(data.title || {}) },
    subtitle: { ...emptyMl(), ...(data.subtitle || {}) },
    images: data.images || [],
    video_url: data.video_url || "",  // Optional: YouTube / Vimeo / direct MP4 URL
    video_title: { ...emptyMl(), ...(data.video_title || {}) },
  });
  const [lang, setLang] = useState("it");
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const setMl = (field, l, v) => setForm((p) => ({ ...p, [field]: { ...p[field], [l]: v } }));

  const upload = async (files) => {
    setUploading(true);
    const out = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", "gallery");
        const res = await api.post("/admin/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(res.data.url);
      } catch (err) {
        toast.error(`${file.name}: errore`);
      }
    }
    if (out.length) {
      setForm((p) => ({ ...p, images: [...p.images, ...out] }));
      toast.success(`${out.length} foto caricate`);
    }
    setUploading(false);
  };

  const move = (i, dir) => setForm((p) => {
    const arr = [...p.images];
    const t = i + dir;
    if (t < 0 || t >= arr.length) return p;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    return { ...p, images: arr };
  });

  // Drag & drop reorder (HTML5 native API — no extra dep needed).
  const onDragStart = (i) => setDragIndex(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (target) => {
    if (dragIndex === null || dragIndex === target) { setDragIndex(null); return; }
    setForm((p) => {
      const arr = [...p.images];
      const [moved] = arr.splice(dragIndex, 1);
      arr.splice(target, 0, moved);
      return { ...p, images: arr };
    });
    setDragIndex(null);
  };

  return (
    <div className="space-y-4" data-testid="live-gallery-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F]">{sectionLabel || 'Galleria "Scatti dal vivo"'}</h3>
          <p className="text-xs text-[#635F59]">{sectionHelp || 'Visualizzata in fondo alla pagina, dopo i Contatti.'}</p>
        </div>
        <div className="flex gap-1">
          {LANGS.map((l) => (
            <button key={l} type="button" onClick={() => setLang(l)} className={`px-2.5 py-1 rounded text-xs font-semibold ${lang === l ? "bg-[#1E3A5F] text-white" : "bg-[#FDFBF7] border border-[#E8E2D9]"}`}>
              {LANG_LABEL[l]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label>Titolo ({LANG_LABEL[lang]})</Label>
          <Input value={form.title[lang] || ""} onChange={(e) => setMl("title", lang, e.target.value)} className="mt-1.5" data-testid="lg-title" />
        </div>
        <div>
          <Label>Sottotitolo ({LANG_LABEL[lang]})</Label>
          <Input value={form.subtitle[lang] || ""} onChange={(e) => setMl("subtitle", lang, e.target.value)} className="mt-1.5" data-testid="lg-subtitle" />
        </div>
      </div>

      <div>
        <Label>Foto ({form.images.length}) <span className="text-[10px] text-[#635F59] font-normal ml-2">💡 Trascina le foto per riordinarle</span></Label>
        <div className="mt-2 flex gap-2 flex-wrap">
          <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)} data-testid="lg-pick-from-library">
            <Library className="w-4 h-4 mr-1" /> Scegli dall'archivio
          </Button>
        </div>
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {form.images.map((url, i) => (
            <div
              key={`im-${i}`}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(i)}
              onDragEnd={() => setDragIndex(null)}
              className={`relative aspect-square rounded overflow-hidden border-2 group cursor-move transition ${dragIndex === i ? "border-[#AC5D45] opacity-50 scale-95" : "border-[#E8E2D9] hover:border-[#1E3A5F]"}`}
              data-testid={`lg-img-${i}`}
              title="Trascina per riordinare"
            >
              <img src={url} alt="" className="w-full h-full object-cover pointer-events-none" />
              <span className="absolute top-1 left-1 bg-black/60 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">{i + 1}</span>
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
                {i > 0 && <button onClick={() => move(i, -1)} className="bg-white/90 rounded px-1.5 py-1 text-xs">←</button>}
                {i < form.images.length - 1 && <button onClick={() => move(i, 1)} className="bg-white/90 rounded px-1.5 py-1 text-xs">→</button>}
                <button onClick={() => setForm((p) => ({ ...p, images: p.images.filter((_, j) => j !== i) }))} className="bg-rose-600 text-white rounded px-1.5 py-1 text-xs">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
          <label className="aspect-square rounded border-2 border-dashed border-[#E8E2D9] flex flex-col items-center justify-center text-[#635F59] hover:border-[#AC5D45] hover:text-[#AC5D45] transition cursor-pointer" data-testid="lg-upload-label">
            <Upload className="w-6 h-6 mb-1" />
            <span className="text-[10px]">{uploading ? "..." : "Carica"}</span>
            <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) upload(f); }} />
          </label>
        </div>
      </div>

      {/* === Video struttura — opzionale === */}
      <div className="border-t border-[#E8E2D9] pt-4 mt-2">
        <Label className="flex items-center gap-2">
          <Film className="w-4 h-4 text-[#AC5D45]" />
          Video struttura <span className="text-[10px] text-[#635F59] font-normal">(opzionale — apparirà sul sito sotto la galleria)</span>
        </Label>
        <Input
          value={form.video_url}
          onChange={(e) => setForm((p) => ({ ...p, video_url: e.target.value }))}
          placeholder="https://youtube.com/watch?v=... oppure https://vimeo.com/... oppure URL .mp4 diretto"
          className="mt-1.5"
          data-testid="lg-video-url"
        />
        <div className="mt-2">
          <Label className="text-xs">Titolo video ({LANG_LABEL[lang]})</Label>
          <Input
            value={form.video_title[lang] || ""}
            onChange={(e) => setMl("video_title", lang, e.target.value)}
            placeholder="es. Tour virtuale del B&B"
            className="mt-1"
            data-testid="lg-video-title"
          />
        </div>
        {form.video_url && (
          <div className="mt-3 aspect-video max-w-md rounded-lg overflow-hidden border border-[#E8E2D9] bg-black">
            {(() => {
              const yt = form.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?#]+)/);
              const vm = form.video_url.match(/vimeo\.com\/(\d+)/);
              if (yt) {
                return <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${yt[1]}`} title="Preview" allow="autoplay; encrypted-media" />;
              }
              if (vm) {
                return <iframe className="w-full h-full" src={`https://player.vimeo.com/video/${vm[1]}`} title="Preview" allow="autoplay" />;
              }
              return <video src={form.video_url} controls className="w-full h-full" />;
            })()}
          </div>
        )}
      </div>

      <Button onClick={() => onSave(form)} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="lg-save">
        <Save className="w-4 h-4 mr-2" /> Salva
      </Button>
      <PhotoPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        multiple
        defaultCategory="gallery"
        onSelect={(urls) => {
          const arr = Array.isArray(urls) ? urls : [urls];
          setForm((p) => ({ ...p, images: [...p.images, ...arr] }));
          toast.success(`${arr.length} foto aggiunte`);
        }}
      />
    </div>
  );
}


/* ---------------- Hero (Home image) ---------------- */
const DEFAULT_HERO = "https://images.unsplash.com/photo-1692650679395-4e2cb1cdeb2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

function HeroEditor({ data, onSave }) {
  const [image, setImage] = useState(data?.image || "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const preview = image?.trim() || DEFAULT_HERO;
  const isCustom = !!image?.trim();

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "hero");
      fd.append("tags", "hero, home");
      const res = await api.post("/admin/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(res.data.url);
      toast.success("Foto caricata — ricordati di salvare");
    } catch (err) {
      toast.error(errMsg(err, "Errore upload"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="hero-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F]">Immagine principale della Home</h3>
          <p className="text-xs text-[#635F59]">
            È la foto grande che si vede appena un visitatore arriva sul sito (lato destro dell'hero).
            Cambiala stagionalmente per rinfrescare il look (estate, inverno, eventi…).
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-[#635F59]">Anteprima</Label>
          <div className="relative aspect-[4/5] max-h-[480px] rounded-xl overflow-hidden border border-[#E8E2D9] bg-[#FDFBF7]">
            <img src={preview} alt="Hero homepage" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F]/30 via-transparent to-[#AC5D45]/15" />
            {!isCustom && (
              <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded uppercase tracking-wide">
                Default Unsplash
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="bg-[#AC5D45] hover:bg-[#8A4936] text-white justify-start"
              data-testid="hero-pick-from-library"
            >
              <Library className="w-4 h-4 mr-2" /> Scegli dall'archivio
            </Button>
            <label className="inline-flex items-center justify-start gap-2 h-10 px-4 rounded-md cursor-pointer bg-[#1E3A5F] text-white hover:bg-[#152A45] transition text-sm font-semibold" data-testid="hero-upload-label">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="hidden"
                disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }}
              />
              <Upload className="w-4 h-4" />
              {uploading ? "Caricamento…" : "Carica nuova foto"}
            </label>
            <div className="flex gap-2">
              <Input
                value={image}
                onChange={(e) => setImage(e.target.value)}
                placeholder="…oppure incolla un URL"
                data-testid="hero-image-url"
              />
              {isCustom && (
                <Button type="button" variant="outline" onClick={() => setImage("")} title="Ripristina default" data-testid="hero-reset">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          <p className="text-[11px] text-[#635F59]">
            💡 Consiglio: scegli una foto verticale o quadrata ad alta risoluzione. La foto verrà mostrata
            a tutta altezza sul lato destro. Per il look migliore usa scatti del mare di Reggio, della
            struttura, o paesaggi della Calabria.
          </p>

          <div className="pt-2">
            <Button onClick={() => onSave({ image: image.trim() })} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="hero-save">
              <Save className="w-4 h-4 mr-2" /> Salva
            </Button>
          </div>
        </div>
      </div>

      <PhotoPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        defaultCategory="hero"
        onSelect={(url) => setImage(url)}
        title="Scegli la foto principale della Home"
      />
    </div>
  );
}
